import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideToast } from '../store/uiSlice';
import { getStatusLabel, getStatusClass, STATUS_FROM_API, formatApiTime, STATUS_PIPELINE, PHLEBONLY_PIPELINE, PHLEBONLY_STATUS_LABEL, getPhlebOnlyStatusClass, STATUS_PIPELINE_TRANSIT, STATUS_PIPELINE_TRANSIT_DROP } from '../constants/status';
import { utcTimeToLocal } from '../Utils';

export function Toast() {
  const dispatch = useDispatch();
  const { toastMessage, toastVisible } = useSelector((state) => state.ui);
  useEffect(() => {
    if (toastVisible) {
      const t = setTimeout(() => dispatch(hideToast()), 2200);
      return () => clearTimeout(t);
    }
  }, [toastVisible, dispatch]);
  return <div className={`toast ${toastVisible ? 'show' : ''}`}>{toastMessage}</div>;
}

// ─── StatusBadge ─────────────────────────────────────────────
export function StatusBadge({ status, destSelected }) {
  const label = getStatusLabel(status);
  const cls = getStatusClass(status);
  return (
    <span className={`stop-badge ${cls}`}>
      {label}
    </span>
  );
}

// ─── PriorityBadge ───────────────────────────────────────────
export function PriorityBadge() {
  return (
    <span className="priority-badge">
      <svg viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
      Stat
    </span>
  );
}

// ─── StatusPipeline ──────────────────────────────────────────
export function StatusPipeline({ status, isInTransit = false, isDropPath = false }) {
  const pipelineMap = isDropPath ? STATUS_PIPELINE_TRANSIT_DROP : (isInTransit ? STATUS_PIPELINE_TRANSIT : STATUS_PIPELINE);
  const PIPELINE_ORDER = Object.keys(pipelineMap);

  const currentStatus = status[status.length - 1]?.status;
  const currentIndex = PIPELINE_ORDER.indexOf(currentStatus);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: '4px 0 8px', overflowX: 'auto' }}>
      {PIPELINE_ORDER.map((key, i) => {
        const done = i <= currentIndex;
        const cur = i === currentIndex;
        return (
          <React.Fragment key={key}>
            {i !== 0 && <div className={`pipeline-line ${done ? 'done' : ''}`} />}
            <div className="pipeline-step">
              <div className={`pipeline-dot ${done ? 'done' : ''} ${cur ? 'current' : ''}`}>
                {done ? <span style={{ color: 'white' }}>✓</span> : <span style={{ color: '#bbb' }}>{i + 1}</span>}
              </div>
              <div className={`pipeline-label ${cur ? 'active' : ''}`}>{pipelineMap[key]}</div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── ActivityTimeline ────────────────────────────────────────
export function ActivityTimeline({ timelines = [], phlebName, driverName, isDriver = false, isPhlebOnly = false, phlebId, isAdmin=false }) {
  if (!timelines || !timelines.length) {
    return (
      <div style={{ fontSize: 13, color: 'var(--bodygray)', textAlign: 'center', padding: '12px 0' }}>
        No activity yet
      </div>
    );
  }

  return (
    <>
      {timelines.map((ev, i) => {
        const role = isPhlebOnly ? 'phleb'
          : ev.status === 'ready' ? 'phleb'
          : ev.role ? ev.role
          : (phlebId && ev.userId === phlebId && ['delivered'].includes(ev.status)) ? 'phleb'
          : (['picked_up', 'delivered'].includes(ev.status)) ? 'driver'
          : 'phleb';
        const actorLabel = role === 'driver' ? 'Courier' : 'Phlebotomist';
        const actorName  = role === 'driver' ? (driverName ?? '') : (phlebName ?? '');

        const eventLabel = isPhlebOnly
          ? (PHLEBONLY_PIPELINE[ev.status] ?? STATUS_FROM_API[ev.status] ?? ev.status)
          : (STATUS_FROM_API[ev.status] ?? ev.status);

        return (
          <div key={i} className="timeline-item">
            <div className="tl-line-wrap">
              <div className="tl-dot" />
              {i < timelines.length - 1 && <div className="tl-vline" />}
            </div>
            <div style={{ flex: 1 }}>
              <div className="tl-event">{actorLabel} — {eventLabel}</div>
              <div className="tl-time">
                {actorName && `${actorName} · `}
                {utcTimeToLocal(ev.time)}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─── DrawListBanner ──────────────────────────────────────────
export function DrawListBanner({ captured, onCapture }) {
  return (
    <div className={`draw-list-banner ${captured ? 'captured' : ''}`} onClick={onCapture}>
      <div className="draw-list-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={captured ? '#1a7a40' : 'var(--berry)'} strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="m21 15-5-5L5 21" />
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <div className="draw-list-title" style={{ color: captured ? '#1a7a40' : 'var(--rp)' }}>
          {captured ? 'Draw List Captured ✓' : 'Snap Draw List'}
        </div>
        <div className="draw-list-sub">
          {captured ? 'Tap to view' : "Take a photo of the facility's draw list before starting"}
        </div>
      </div>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={captured ? '#1a7a40' : 'var(--berry)'} strokeWidth="2" strokeLinecap="round">
        <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    </div>
  );
}

// ─── RouteSortBar ────────────────────────────────────────────
export function RouteSortBar({ sortMode, onSort }) {
  const options = [
    { key: 'route', label: 'Route Order' },
    { key: 'time', label: 'Pickup Time' },
    { key: 'priority', label: 'Stat First' },
  ];
  return (
    <div className="sort-bar">
      <div className="sort-label">Sort by:</div>
      <div className="sort-toggle">
        {options.map((o) => (
          <button key={o.key} className={`sort-btn ${sortMode === o.key ? 'active' : ''}`} onClick={() => onSort(o.key)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function resolveSrc(p) {
  return p?.previewUrl || p?.url || (p?.fileKey ? `/api${p.fileKey}` : null);
}

export function PhotoLightbox() {
  const dispatch = useDispatch();
  const { lightboxOpen, lightboxPhotos, lightboxIdx, lightboxTitle, lightboxSubtitle } = useSelector((state) => state.ui);
  const { closeLightbox, setLightboxIdx } = require('../store/uiSlice');
  if (!lightboxOpen) return null;
  const photo = lightboxPhotos[lightboxIdx];
  const src = resolveSrc(photo);
  return (
    <div className="photo-lightbox open">
      <div className="lb-header">
        <button className="lb-close" onClick={() => dispatch(closeLightbox())}>← Back</button>
        <div>
          <div className="lb-title">{lightboxTitle}</div>
          <div className="lb-subtitle">{lightboxSubtitle}</div>
        </div>
      </div>
      <div className="lb-main">
        <div className="lb-img-frame">
          {src ? (
            <img
              src={src}
              alt={photo?.label || 'Facility photo'}
              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
            />
          ) : (
            <div className="lb-img-placeholder">
              <div className="lb-camera-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(200,160,210,0.9)" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </div>
              <div className="lb-img-label">{photo?.label}</div>
              <div className="lb-timestamp">No image available</div>
            </div>
          )}
        </div>
      </div>
      <div className="lb-caption">Photo {lightboxIdx + 1} of {lightboxPhotos.length} · {photo?.label}</div>
      <div className="lb-thumbs">
        {lightboxPhotos.map((p, i) => {
          const thumbSrc = resolveSrc(p);
          return (
            <div key={p.id || i} className={`lb-thumb ${i === lightboxIdx ? 'active' : ''}`} onClick={() => dispatch(setLightboxIdx(i))}>
              {thumbSrc ? (
                <img
                  src={thumbSrc}
                  alt={p.label}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(200,160,210,0.7)" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              )}
              <div className="lb-thumb-label">{p.label?.split(' ').slice(0, 2).join(' ')}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CameraModal ─────────────────────────────────────────────
export { default as CameraModal } from './CameraModal';